import { useQuery } from '@tanstack/react-query';
import { trackingService, type TrackingStats } from '../../services/tracking.service';
import { resolveDatePreset } from '../../utils/date-presets';
import { dayRangeParams } from '../../utils/timezone';
import { useReportDefaults } from '../../hooks/useReportDefaults';
import { useWorkspaceStore } from '../../stores/workspace.store';
import { NIL_UUID, levelDims } from '../tracking/tracking-page.format';
import { TIME_DIMS, ENTITY_FILTER_FIELDS, PIVOT_SERIES_CAP, RATES, pivotDims, dimValue, dimName, type PivotSeries } from './format';
import type { DashboardConfig, DashboardPanel } from './types';

export type PanelStats = TrackingStats & { series?: PivotSeries[]; truncated?: boolean };

/** Resolve a panel's saved query against the dashboard's relative time range and fetch it. */
export function usePanelData(dashboard: DashboardConfig, panel: DashboardPanel) {
  const q = panel.query;
  // A panel that follows the global selection must refetch when that selection changes;
  // the interceptor already sends the ids, but the query key has to move too.
  const selectedIds = useWorkspaceStore((s) => s.selectedIds);
  // Per-panel range overrides the dashboard's when set.
  const bounds = dayRangeParams(resolveDatePreset(q.time_preset ?? dashboard.time_preset));
  // Per-panel timezone, else the dashboard's, else the viewer's own report-default tz — the
  // same fallback the Tracking report uses, so an unset panel matches their other reports.
  const defaults = useReportDefaults();
  const tz = q.timezone ?? dashboard.timezone ?? defaults.timezone;
  const excludeBots = q.exclude_bots ?? true;
  const dims = levelDims(q.group_by);
  const isTime = dims.length === 1 && TIME_DIMS.has(dims[0]);
  const pivot = panel.viz === 'line' || panel.viz === 'bar' ? pivotDims(q.group_by) : null;
  // Time dimensions want the full sequence in order; others cap (a chart with 200 slices is noise).
  const timeDim = pivot?.time ?? q.group_by;
  const timeLimit = timeDim === 'hour' ? 24 : timeDim === 'date_hour' ? 168 : 90;
  const limit = q.limit ?? (panel.viz === 'stat' ? 1 : pivot ? 1000 : isTime ? timeLimit : panel.viz === 'table' ? 50 : 12);

  // Collapse FilterEntry[] → { field: value | value[] } the same way the report does.
  const filterParams: Record<string, string | string[]> = {};
  for (const f of q.filters ?? []) {
    const ex = filterParams[f.field];
    if (ex === undefined) filterParams[f.field] = f.value;
    else if (Array.isArray(ex)) ex.push(f.value);
    else filterParams[f.field] = [ex, f.value];
  }

  const params = {
    ...bounds,
    group_by: dims,
    ...filterParams,
    timezone: tz,
    // Time dimensions default to chronological (by group, ascending); others to top-by-metric.
    sort_by: pivot ? q.metrics[0] : q.sort_by ?? (isTime ? q.group_by : q.metrics[0]),
    sort_order: pivot ? 'desc' : q.sort_order ?? (isTime ? 'asc' : 'desc'),
    include_bots: !excludeBots,
    conv_time_basis: q.conv_time_basis ?? 'conversion',
    limit,
    ...(q.compare ? { compare: true } : {}),
  } satisfies Parameters<typeof trackingService.getStats>[0];

  const opts = q.workspace_id ? { workspaceId: q.workspace_id } : undefined;
  // Rank series on their own (a rate by volume, or tiny ones win); then fetch only theirs.
  const fetchPivot = async (split: string): Promise<PanelStats> => {
    const top = await trackingService.getStats(
      { ...params, group_by: [split], sort_by: RATES.has(q.metrics[0]) ? 'visits' : q.metrics[0], sort_order: 'desc', limit: PIVOT_SERIES_CAP, compare: undefined }, opts);
    const series = top.rows.map((r) => ({ value: dimValue(r, split), name: dimName(r, split) }));
    // Only entity filters take a list, and one with a real id drops (none)'s impression spend.
    const narrow = ENTITY_FILTER_FIELDS.includes(split) && series.every((s) => s.value && s.value !== NIL_UUID);
    const history = await trackingService.getStats({ ...params, ...(narrow ? { [split]: series.map((s) => s.value) } : {}) }, opts);
    return { ...history, series, truncated: history.total_row_count > history.rows.length };
  };

  return useQuery<PanelStats>({
    queryKey: ['dashboard-panel', dashboard.time_preset, tz, panel.viz, q.workspace_id ?? (selectedIds.join(',') || 'all'), JSON.stringify(q)],
    queryFn: () => (pivot ? fetchPivot(pivot.split) : trackingService.getStats(params, opts)),
    placeholderData: (prev) => prev,
    staleTime: 60_000,
    // Summary panels render the precomputed briefing, not a stats query.
    enabled: panel.viz !== 'summary',
  });
}
